import type {
  AccessChecker,
  NavGroup,
  NavLeaf,
  NavNode,
  PathBuilder,
  Translate,
} from './types';

import { BriefcaseBusinessIcon } from '@valwin/facto/icons';

import { ROUTES } from '~shared/routes';

function buildExtranetNetworkGroup(
  buildPath: PathBuilder,
  t: Translate,
  checkAccess: AccessChecker
): NavGroup {
  const children: NavLeaf[] = [];

  if (
    checkAccess({
      isAdmin: true,
      requiredFeatures: ['EXTRANET_GROUP_PRESENTATION'],
    })
  ) {
    children.push({
      kind: 'link',
      id: 'extranet-staff',
      label: t('menu whoAreWe'),
      to: buildPath(ROUTES.extranet.staff),
    });
  }

  if (
    checkAccess({ isAdmin: true, requiredFeatures: ['EXTRANET_GROUP_NEWS'] })
  ) {
    children.push(
      {
        kind: 'link',
        id: 'extranet-events',
        label: t('menu events'),
        to: buildPath(ROUTES.extranet.events.root),
      },
      {
        kind: 'link',
        id: 'extranet-actus',
        label: t('menu news'),
        to: buildPath(ROUTES.extranet.actus.root),
      }
    );
  }

  if (
    checkAccess({
      isAdmin: true,
      requiredFeatures: ['EXTRANET_GROUP_NEWSLETTERS'],
    })
  ) {
    children.push({
      kind: 'link',
      id: 'extranet-newsletters',
      label: t('menu newsletters'),
      to: buildPath(ROUTES.extranet.newsletters),
    });
  }

  return {
    kind: 'group',
    id: 'extranet-network',
    label: t('menu networkLife'),
    children,
  };
}

function buildExtranetLabGroup(
  buildPath: PathBuilder,
  t: Translate,
  checkAccess: AccessChecker
): NavGroup {
  const children: NavLeaf[] = [];

  if (
    checkAccess({
      isAdmin: true,
      requiredFeatures: ['EXTRANET_LAB_PARTNERS_MANDATORY_DEALS'],
    })
  ) {
    children.push({
      kind: 'link',
      id: 'extranet-mandatory',
      label: t('menu mandatoryAgreements'),
      to: buildPath(ROUTES.extranet.agreements.mandatory.root),
    });
  }

  if (
    checkAccess({
      isAdmin: true,
      requiredFeatures: [
        'EXTRANET_LAB_PARTNERS_OTHER_DEALS',
        'COS_EXTRANET_LAB_PARTNERS_PRIORITY_DEALS',
      ],
    })
  ) {
    children.push({
      kind: 'link',
      id: 'extranet-priority',
      label: t('menu priorityAgreements'),
      to: buildPath(ROUTES.extranet.agreements.priority.root),
    });
  }

  if (
    checkAccess({
      isAdmin: true,
      requiredFeatures: ['COS_EXTRANET_LAB_PARTNERS_RECOMMENDED_DEALS'],
    })
  ) {
    children.push({
      kind: 'link',
      id: 'extranet-recommended',
      label: t('menu recommendedAgreements'),
      to: buildPath(ROUTES.extranet.agreements.recommended.root),
    });
  }

  if (
    checkAccess({
      isAdmin: true,
      requiredFeatures: ['EXTRANET_LAB_PARTNERS_NEWS'],
    })
  ) {
    children.push({
      kind: 'link',
      id: 'extranet-actus-labo',
      label: t('menu labNews'),
      to: buildPath(ROUTES.extranet.actusLabo.root),
    });
  }

  return {
    kind: 'group',
    id: 'extranet-lab',
    label: t('menu labInfo'),
    children,
  };
}

export function buildExtranetGroup(
  buildPath: PathBuilder,
  t: Translate,
  checkAccess: AccessChecker
): NavGroup {
  const hasExtranetGroup = checkAccess({
    isAdmin: true,
    requiredFeatures: [
      'EXTRANET_GROUP_PRESENTATION',
      'EXTRANET_GROUP_NEWS',
      'EXTRANET_GROUP_NEWSLETTERS',
    ],
  });
  const hasExtranetLab = checkAccess({
    isAdmin: true,
    requiredFeatures: [
      'EXTRANET_LAB_PARTNERS_MANDATORY_DEALS',
      'EXTRANET_LAB_PARTNERS_OTHER_DEALS',
      'COS_EXTRANET_LAB_PARTNERS_PRIORITY_DEALS',
      'COS_EXTRANET_LAB_PARTNERS_RECOMMENDED_DEALS',
      'EXTRANET_LAB_PARTNERS_NEWS',
    ],
  });

  const children: NavNode[] = [
    {
      kind: 'link',
      id: 'extranet-directory',
      label: t('menu directories'),
      to: buildPath(ROUTES.extranet.directory.root),
    },
  ];

  if (checkAccess({ isAdmin: true, requiredFeatures: ['EXTRANET_BANNERS'] })) {
    children.push({
      kind: 'link',
      id: 'extranet-banners',
      label: t('menu banners'),
      to: buildPath(ROUTES.extranet.banners),
    });
  }

  if (checkAccess({ isAdmin: true, requiredFeatures: ['EXTRANET_TASKS'] })) {
    children.push({
      kind: 'link',
      id: 'extranet-tasks',
      label: t('menu tasks'),
      to: buildPath(ROUTES.extranet.tasks.root),
    });
  }

  if (hasExtranetGroup) {
    children.push(buildExtranetNetworkGroup(buildPath, t, checkAccess));
  }

  if (hasExtranetLab) {
    children.push(buildExtranetLabGroup(buildPath, t, checkAccess));
  }

  if (
    checkAccess({ isAdmin: true, requiredFeatures: ['COS_EXTRANET_TOOLS'] })
  ) {
    children.push({
      kind: 'link',
      id: 'extranet-tools',
      label: t('menu tools'),
      to: buildPath(ROUTES.extranet.tools.root),
    });
  }

  if (checkAccess({ isAdmin: true, requiredFeatures: ['EXTRANET_PARTNERS'] })) {
    children.push({
      kind: 'link',
      id: 'extranet-partners',
      label: t('menu partners'),
      to: buildPath(ROUTES.extranet.partners.root),
    });
  }

  if (
    checkAccess({ isAdmin: true, requiredFeatures: ['EXTRANET_MEDIA_LIBRARY'] })
  ) {
    children.push({
      kind: 'link',
      id: 'extranet-medias',
      label: t('menu mediaLibrary'),
      to: buildPath(ROUTES.extranet.medias),
    });
  }

  children.push(
    {
      kind: 'link',
      id: 'extranet-faq',
      label: t('menu faq'),
      to: buildPath(ROUTES.extranet.faq),
    },
    {
      kind: 'link',
      id: 'extranet-settings',
      label: t('menu settings'),
      to: buildPath(ROUTES.extranet.settings),
    }
  );

  return {
    kind: 'group',
    id: 'extranet',
    label: t('menu extranet'),
    icon: BriefcaseBusinessIcon,
    children,
  };
}
